import React, { useState } from 'react';
import Router from 'next/router';
import Auth from '@aws-amplify/auth';
import { Formik, Form, Field } from 'formik';
import * as yup from 'yup';
import {
  Alert,
  AlertIcon,
  Button,
  Center,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Flex,
  InputGroup,
  InputRightElement,
  Input,
  Spacer,
  Text,
  useColorModeValue
} from '@chakra-ui/react';
import withLogger, { Logger } from '../libs/logger';

type VerifyCodeFormProps = {
  user: {
    username: string;
    password: string;
  };
  log: Logger;
};

function VerifyCodeForm({ log, user }: VerifyCodeFormProps) {
  const [error, setError] = useState('');

  async function onConfirmCode(evt: any) {
    const { code } = evt;

    setError('');

    // try to confirm verfication code
    const codeConfirmed = await Auth.confirmSignUp(user.username, code).catch((err) => {
      log.error({ action: 'onConfirmCode:Auth.confirmSignUp:error', error: err });
      setError('Could not confirm code.');
    });

    // returned if not verified
    if (!codeConfirmed) {
      return;
    }

    // try to auto sign in
    const signedIn = await Auth.signIn(user.username, user.password).catch((e) => {
      log.error({ action: 'onEmailVerified:Auth.signIn:error', error: e });
      setError('Could not sign in.');
    });

    // return if could not sign in
    if (!signedIn) {
      return;
    }

    // Route to account on successful login
    await Router.push('/account').catch((e) => {
      log.error({ action: 'onEmailVerified:route:error', error: e });
      setError('Could not load account page');
    });
  }

  const errorMessage = error.length > 0 && (
    <Alert status='error'>
      <AlertIcon />
      {error}
    </Alert>
  );

  const emailCodeSchema = yup.object({
    code: yup
      .string()
      .matches(/^\d{6}$/, 'Not a valid code')
      .required('Verification code is required')
  });

  function ConfirmEmailForm() {
    return (
      <Formik validationSchema={emailCodeSchema} onSubmit={onConfirmCode} initialValues={{ code: '' }}>
        {({ isSubmitting, handleSubmit, handleChange, handleBlur, values, errors, touched }) => (
          <Form noValidate onSubmit={handleSubmit} color='gray.400'>
            {errorMessage}
            <Field name='code'>
              {({ field, form }) => (
                <FormControl mb='8' isInvalid={form.errors.code && form.touched.code}>
                  <FormLabel htmlFor='code' srOnly>
                    Email Verification Code
                  </FormLabel>
                  <Input
                    {...field}
                    id='code'
                    autoComplete='code'
                    placeholder='Email verification code'
                    bg={useColorModeValue('gray.300', 'whiteAlpha.400')}
                    colorScheme='whiteAlpha'
                  />
                  <FormErrorMessage>{form.errors.code}</FormErrorMessage>
                </FormControl>
              )}
            </Field>

            <Text mb='8' color={useColorModeValue('blackAlpha.500', 'whiteAlpha.700')}>
              You will receive a 6 digit code via email.
            </Text>
            <Center>
              <Button
                borderRadius='4px'
                width='100%'
                type='submit'
                isLoading={isSubmitting}
                colorScheme='brand'
                size='lg'
              >
                Verify
              </Button>
            </Center>
          </Form>
        )}
      </Formik>
    );
  }

  return <ConfirmEmailForm />;
}

export default withLogger(VerifyCodeForm, 'component:VerifyCodeForm');
